import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Spin,
  Typography,
  Divider,
  Space,
  DatePicker,
  Radio,
  Button,
  Alert,
  message,
  Empty
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  MessageOutlined,
  ReloadOutlined,
  DownloadOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { SidebarLayout } from '../components/SidebarLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Icons } from '../components/Icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Sector,
  Cell,
  BarChart,
  Bar,
  Label
} from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import html2canvas from 'html2canvas';
import '../styles/dashboard.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const COLORS = [
  '#1976D2', '#2196F3', '#64B5F6', '#90CAF9', '#BBDEFB',
  '#0D47A1', '#1565C0', '#1E88E5', '#42A5F5', '#81D4FA'
];

const LINE_COLORS = {
  messages: '#1976D2'
};

const GeneralDashboard = () => {
  const { token, selectedClient } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [days, setDays] = useState(30);
  const [chartViewMode, setChartViewMode] = useState('line');
  const [messageApi, contextHolder] = message.useMessage();  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (selectedClient) {
      fetchDashboardData();
    }
  }, [selectedClient, days]);

  useEffect(() => {
    if (selectedClient && days && !loading) {
      messageApi.info(`Exibindo dados dos últimos ${days} dias`);
    }
  }, [days, selectedClient]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/customers/general-dashboard`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          params: {
            selectedClient: selectedClient.client.id,
            days: days
          }
        }
      );
      
      const processedData = {
        horses_count: response.data.horses_count || 0,
        traylers_count: response.data.traylers_count || 0,
        devices_count: response.data.devices_count || 0,
        iot_messages_count: response.data.iot_messages_count || 0,
        horses_by_type: response.data.horses_by_type || [],
        traylers_by_type: response.data.traylers_by_type || [],
        devices_by_type: response.data.devices_by_type || [],
        messages_by_day: response.data.messages_by_day || []
      };

      setDashboardData(processedData);
      setLoading(false);
    } catch (error) {
      messageApi.error('Erro ao carregar dados do dashboard');
      setLoading(false);
      console.error('Erro:', error);
    }
  };

  const handleDaysChange = (value) => {
    setDays(value);
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num;
  };

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle,
      fill, payload, percent, value } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} className="recharts-text">
          {payload.type}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{`${value} unid.`}</text>
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  return (
    <SidebarLayout>
      {contextHolder}      <div id="general-dashboard" style={{ padding: '20px', minHeight: '100vh' }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Title level={2} style={{ margin: 0 }}>
              Dashboard Geral
            </Title>
            
          </Col>          
        </Row>        <Spin spinning={loading} tip="Carregando dados...">
          {dashboardData ? (
            <>
              
              
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} hoverable className="stat-card" onClick={() => window.location.href = '/truck_horses'} style={{ cursor: 'pointer' }}>
                    <Statistic
                      title={<span><img src={Icons.horseBlack} alt="Cavalo" style={{ width: 16, marginRight: 8 }} /> Cavalos</span>}
                      value={dashboardData.horses_count}
                      valueStyle={{ color: '#1976D2' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} hoverable className="stat-card" onClick={() => window.location.href = '/truck_traylers'} style={{ cursor: 'pointer' }}>
                    <Statistic
                      title={<span><img src={Icons.traylerBlack} alt="Carreta" style={{ width: 16, marginRight: 8 }} /> Carretas</span>}
                      value={dashboardData.traylers_count}
                      valueStyle={{ color: '#2196F3' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} hoverable className="stat-card" onClick={() => window.location.href = '/devices'} style={{ cursor: 'pointer' }}>
                    <Statistic
                      title={<span><img src={Icons.deviceBlack} alt="Dispositivo" style={{ width: 16, marginRight: 8 }} /> Dispositivos</span>}
                      value={dashboardData.devices_count}
                      valueStyle={{ color: '#0D47A1' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card bordered={false} hoverable className="stat-card" onClick={() => window.location.href = '/reports'} style={{ cursor: 'pointer' }}>
                    <Statistic
                      title={<span><MessageOutlined style={{color: 'black'}} /> Mensagens IoT</span>}
                      value={formatNumber(dashboardData.iot_messages_count)}
                      valueStyle={{ color: '#1565C0' }}
                    />
                  </Card>
                </Col></Row>              <style jsx="true">{`
                .stat-card {
                  transition: all 0.3s ease;
                  animation: fadeIn 0.8s ease-in-out;
                }
                .stat-card:hover {
                  transform: translateY(-5px);
                  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
                }
                @keyframes fadeIn {
                  from { opacity: 0; transform: translateY(10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                .recharts-cartesian-axis-tick text {
                  font-size: 11px;
                  visibility: visible !important;
                }
                .recharts-wrapper {
                  margin: 0 auto;
                }
                .recharts-yAxis .recharts-cartesian-axis-tick {
                  font-size: 10px;
                }
              `}</style>

              <Divider />

              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Card 
                    title={
                      <Space>
                        <img src={Icons.horseBlack} alt="Cavalo" style={{ width: 16, marginRight: 4 }} />
                        <span>Cavalos por Tipo</span>
                      </Space>
                    }
                    className="dashboard-card"
                    style={{ height: '100%' }}                  >                    {dashboardData.horses_by_type && dashboardData.horses_by_type.length > 0 ? (
                      <ResponsiveContainer width="100%" height={225} className="responsive-chart"><BarChart
                          data={dashboardData.horses_by_type}
                          margin={{
                            top: 10,
                            right: 10,
                            left: 0,
                            bottom: 30,
                          }}
                          barCategoryGap={5}
                          maxBarSize={50}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="type" 
                            tick={{ fontSize: 11, angle: -45, textAnchor: 'end' }}
                            height={40}
                            interval={0}
                          />
                          <YAxis width={30} />
                          <RechartsTooltip
                            formatter={(value, name) => [value, 'Quantidade']}
                          />                          <Bar 
                            dataKey="count" 
                            name="Quantidade" 
                            fill="#1976D2"
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                            label={{ position: 'top', fill: '#666', fontSize: 11 }}
                          >
                            {dashboardData.horses_by_type.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="Sem dados de cavalos para exibir" />
                    )}
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card 
                    title={
                      <Space>
                        <img src={Icons.traylerBlack} alt="Carreta" style={{ width: 16, marginRight: 4 }} />
                        <span>Carretas por Tipo</span>
                      </Space>
                    }
                    className="dashboard-card"
                    style={{ height: '100%' }}                  >                    {dashboardData.traylers_by_type && dashboardData.traylers_by_type.length > 0 ? (
                      <ResponsiveContainer width="100%" height={225} className="responsive-chart"><BarChart
                          data={dashboardData.traylers_by_type}
                          margin={{
                            top: 10,
                            right: 10,
                            left: 0,
                            bottom: 30,
                          }}
                          barCategoryGap={5}
                          maxBarSize={50}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="type" 
                            tick={{ fontSize: 11, angle: -45, textAnchor: 'end' }}
                            height={40}
                            interval={0}
                          />
                          <YAxis width={30} />
                          <RechartsTooltip
                            formatter={(value, name) => [value, 'Quantidade']}
                          />                          <Bar 
                            dataKey="count" 
                            name="Quantidade" 
                            fill="#2196F3"
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                            label={{ position: 'top', fill: '#666', fontSize: 11 }}
                          >
                            {dashboardData.traylers_by_type.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="Sem dados de carretas para exibir" />
                    )}
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card 
                    title={
                      <Space>
                        <img src={Icons.deviceBlack} alt="Dispositivo" style={{ width: 16, marginRight: 4 }} />
                        <span>Dispositivos por Tipo</span>
                      </Space>
                    }
                    className="dashboard-card"
                    style={{ height: '100%' }}                  >                    {dashboardData.devices_by_type && dashboardData.devices_by_type.length > 0 ? (
                      <ResponsiveContainer width="100%" height={225} className="responsive-chart"><BarChart
                          data={dashboardData.devices_by_type}
                          margin={{
                            top: 10,
                            right: 10,
                            left: 0,
                            bottom: 30,
                          }}
                          barCategoryGap={5}
                          maxBarSize={50}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="type" 
                            tick={{ fontSize: 11, angle: -45, textAnchor: 'end' }}
                            height={40}
                            interval={0}
                          />
                          <YAxis width={30} />
                          <RechartsTooltip
                            formatter={(value, name) => [value, 'Quantidade']}
                          />                          <Bar 
                            dataKey="count" 
                            name="Quantidade" 
                            fill="#0D47A1"
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                            label={{ position: 'top', fill: '#666', fontSize: 11 }}
                          >
                            {dashboardData.devices_by_type.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="Sem dados de dispositivos para exibir" />                    )}
                  </Card>
                </Col>
              </Row>

              <Divider />

              {/* Gráfico de mensagens por tempo */}              
              <Card 
                title={
                  <Space>
                    <MessageOutlined style={{color: 'black'}} />
                    <span>Mensagens recebidas ao longo do tempo</span>
                  </Space>
                }
                extra={
                  <Space>
                    <Select
                      defaultValue={30}
                      style={{ width: 140 }}
                      onChange={handleDaysChange}
                      value={days}
                    >
                      <Option value={7}>Últimos 7 dias</Option>
                      <Option value={15}>Últimos 15 dias</Option>
                      <Option value={30}>Últimos 30 dias</Option>
                      <Option value={60}>Últimos 60 dias</Option>
                      <Option value={90}>Últimos 90 dias</Option>
                    </Select>
                    <Radio.Group 
                      value={chartViewMode}
                      onChange={e => setChartViewMode(e.target.value)}
                      buttonStyle="solid"
                    >
                      <Radio.Button value="line"><LineChartOutlined /> Linha</Radio.Button>
                      <Radio.Button value="bar"><BarChartOutlined /> Barras</Radio.Button>
                    </Radio.Group>
                  </Space>
                }
                className="dashboard-card messages-chart-card"
                style={{ marginBottom: 16, padding: 0 }}
              >                
                {dashboardData.messages_by_day && dashboardData.messages_by_day.length > 0 ? (
                  <div className="messages-chart-responsive-wrapper">
                    <ResponsiveContainer width="100%" height={260} className="responsive-chart">
                      {chartViewMode === 'line' ? (                      
                        <LineChart
                          data={dashboardData.messages_by_day}
                          margin={{
                            top: 10,
                            right: 10,
                            left: 10,
                            bottom: 10,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => dayjs(value).format('DD/MM')}
                            interval="preserveStartEnd"
                          />
                          <YAxis width={40} />
                          <RechartsTooltip 
                            formatter={(value, name) => [value, 'Mensagens']}
                            labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
                          />
                                               
                          <Line
                            type="monotone"
                            dataKey="count"
                            name="Mensagens"
                            stroke={LINE_COLORS.messages}
                            activeDot={{ r: 8 }}
                            strokeWidth={2}
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                          />
                        </LineChart>
                      ) : (                      
                        <BarChart
                          data={dashboardData.messages_by_day}
                          margin={{
                            top: 10,
                            right: 10,
                            left: 10,
                            bottom: 10,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => dayjs(value).format('DD/MM')}
                            interval="preserveStartEnd"
                          />
                          <YAxis width={40} />
                          <RechartsTooltip 
                            formatter={(value, name) => [value, 'Mensagens']}
                            labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
                          />

                          <Bar
                            dataKey="count"
                            name="Mensagens"
                            fill={LINE_COLORS.messages}
                            barSize={20}
                            animationDuration={1500}
                            animationEasing="ease-in-out"
                          />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Empty description="Sem dados de mensagens para exibir" />
                )}
              </Card>
            </>) : (
            <Empty 
              description={
                <span>
                  {selectedClient ? 
                    "Não há dados disponíveis para exibir" : 
                    "Selecione um cliente para visualizar o dashboard"
                  }
                  <br />
                  {!selectedClient && <Alert 
                    message="Nenhum cliente selecionado" 
                    type="info" 
                    showIcon 
                    style={{ marginTop: 16 }}
                  />}
                </span>
              }
              style={{ marginTop: 48 }}
            />
          )}
        </Spin>
      </div>
    </SidebarLayout>
  );
};

export { GeneralDashboard };